/**
 * Seeded random number generator for reproducible floor/encounter generation.
 * Uses a simple linear congruential generator (LCG) algorithm.
 */
export interface SeededRng {
  /** Returns next random number in range [0, 1) */
  next(): number;
  /** Returns random integer in range [min, max) (exclusive max) */
  nextInt(min: number, max: number): number;
  /** Picks a random element from an array */
  pick<T>(array: T[]): T;
}

/**
 * Create a seeded random number generator.
 * Same seed will produce identical sequences.
 * @param seed - Initial seed value
 * @returns SeededRng instance
 */
export function createSeededRng(seed: number): SeededRng {
  // LCG parameters (same as used in many game engines)
  const a = 1664525;
  const c = 1013904223;
  const m = 2 ** 32;

  let state = seed;

  const next = (): number => {
    state = (a * state + c) % m;
    // Convert to [0, 1) range
    return state / m;
  };

  return {
    next,
    nextInt: (min: number, max: number): number => {
      const range = max - min;
      return Math.floor(next() * range) + min;
    },
    pick: <T>(array: T[]): T => {
      if (array.length === 0) {
        throw new Error("Cannot pick from empty array");
      }
      const index = Math.floor(next() * array.length);
      return array[index];
    },
  };
}
